import React, { useState, useEffect } from 'react';
import { useMeeting } from '../contexts/MeetingContext';
import { apiService, handleApiError } from '../services/api';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PDFDocumentViewer from './PDFDocumentViewer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ReadingOrderBadge = ({ readingOrder, category }) => {
  // If no reading order, don't show badge
  if (!readingOrder && !category) return null;
  
  const getBadgeInfo = (category, order) => {
    // Determine badge based on reading order position
    if (order === 1) {
      return { label: 'START HERE', color: 'bg-red-100 text-red-800 border-red-300' };
    } else if (order <= 3 || category === 'PROBLEM_ANALYSIS') {
      return { label: 'Read Early', color: 'bg-red-100 text-red-800 border-red-300' };
    } else if (order <= 10 || category === 'TECHNICAL_DEEP_DIVE') {
      return { label: 'Important', color: 'bg-orange-100 text-orange-800 border-orange-300' };
    } else if (category === 'POSITION_STATEMENT') {
      return { label: 'Read Later', color: 'bg-blue-100 text-blue-800 border-blue-300' };
    } else {
      return { label: 'Can Skip', color: 'bg-gray-100 text-gray-600 border-gray-300' };
    }
  };
  
  const badge = getBadgeInfo(category, readingOrder);
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${badge.color}`}
    >
      <span>{badge.label}</span>
      <span className="ml-1 px-1.5 py-0.5 bg-white bg-opacity-50 rounded">#{readingOrder}</span>
    </div>
  );
};

const CompanyPill = ({ company, onClick, highlighted }) => {
  if (!company) return null;
  
  const getDisplayName = (company) => {
    // Shorten only individual long company names, not comma-separated ones
    const shortNames = {
      'LG Electronics Inc.': 'LG',
      'NTT DOCOMO, INC.': 'NTT DOCOMO',
      'Qualcomm Incorporated': 'Qualcomm',
      'Google Ireland Limited': 'Google',
      'Transsion Holdings': 'Transsion',
      'InterDigital Inc.': 'InterDigital',
      'ZTE Corporation': 'ZTE',
      'Spreadtrum': 'UNISOC'
    };
    
    return shortNames[company] || company;
  };

  return (
    <span 
      onClick={onClick}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
        highlighted 
          ? 'bg-red-600 text-white' 
          : 'bg-green-100 text-green-800 hover:bg-green-200'
      }`}
    >
      {getDisplayName(company)}
    </span>
  );
};

const DocumentBrowser = () => {
  const { selectedMeeting, selectedAgenda } = useMeeting();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [documentViewer, setDocumentViewer] = useState({
    isOpen: false,
    documentId: null,
    verbatimProposals: [],
    subtopicName: null,
    semanticGroupName: null
  });

  useEffect(() => {
    if (selectedMeeting && selectedAgenda) {
      // Clear previous data immediately when agenda changes
      setDocuments([]);
      setFilteredDocuments([]);
      setSearchTerm('');
      setCompanyFilter('');
      loadDocuments();
    }
  }, [selectedMeeting, selectedAgenda]);

  useEffect(() => {
    filterDocuments();
  }, [searchTerm, companyFilter, documents]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📄 Loading documents for', selectedMeeting, selectedAgenda);
      const response = await apiService.getDocuments(selectedMeeting, selectedAgenda);
      
      // Handle response structure - the API returns {documents: [...], ...}
      const documentsData = response.data?.documents || [];
      
      console.log('✅ Loaded', documentsData.length, 'documents');
      setDocuments(documentsData);
      
      // Extract unique companies - split comma-separated companies
      const companies = new Set();
      documentsData.forEach(doc => {
        if (doc.company) {
          // Split by comma and trim whitespace
          const companyList = doc.company.split(',').map(c => c.trim());
          companyList.forEach(company => {
            if (company) {
              companies.add(company);
            }
          });
        }
      });
      setAllCompanies(Array.from(companies).sort());
      
    } catch (error) {
      console.error('❌ Failed to load documents:', error);
      const errorMessage = handleApiError(error, 'Failed to load documents');
      setError(errorMessage);
      setDocuments([]); // Ensure documents is always an array
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.document_id?.toLowerCase().includes(term) ||
        doc.title?.toLowerCase().includes(term)
      );
    }
    
    // Company filter - check if the company is in the document's company list
    if (companyFilter) {
      filtered = filtered.filter(doc => {
        if (!doc.company) return false;
        // Split the document's company string and check if it includes the filter
        const companyList = doc.company.split(',').map(c => c.trim());
        return companyList.includes(companyFilter);
      });
    }
    
    // ALWAYS sort by reading order (importance_score) ascending
    // Documents without scores go to the end
    filtered.sort((a, b) => {
      const scoreA = a.importance_score ?? 999;
      const scoreB = b.importance_score ?? 999;
      return scoreA - scoreB;
    });
    
    setFilteredDocuments(filtered);
  };

  const handleViewPDF = (doc) => {
    console.log('👁️ Opening PDF for document:', doc.document_id);
    setDocumentViewer({
      isOpen: true,
      documentId: doc.document_id,
      verbatimProposals: doc.proposals || [],
      subtopicName: `Document: ${doc.document_id}`,
      semanticGroupName: doc.company
    });
  };

  const closeDocumentViewer = () => {
    setDocumentViewer({
      isOpen: false,
      documentId: null,
      verbatimProposals: [],
      subtopicName: null,
      semanticGroupName: null
    });
  };

  const generateDocumentPDF = (doc) => {
    try {
      const pdf = new jsPDF();
      const vodafoneRed = [230, 0, 0];
      
      // Header
      pdf.setFillColor(...vodafoneRed);
      pdf.rect(0, 0, 210, 25, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Document Proposals Export', 14, 12);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${selectedMeeting} - ${selectedAgenda}`, 14, 19);
      
      // Document Info
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Document: ${doc.document_id}`, 14, 35);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let yPos = 42;
      
      if (doc.company) {
        pdf.text(`Company: ${doc.company}`, 14, yPos);
        yPos += 6;
      }
      
      if (doc.title) {
        pdf.setFontSize(9);
        const titleLines = pdf.splitTextToSize(doc.title, 180);
        pdf.text(titleLines, 14, yPos);
        yPos += (titleLines.length * 5) + 3;
      }
      
      pdf.text(`Total Proposals: ${doc.proposal_count}`, 14, yPos);
      yPos += 6;
      
      if (doc.subtopics && doc.subtopics.length > 0) {
        pdf.text(`Appears in ${doc.subtopics.length} subtopic(s)`, 14, yPos);
        yPos += 8;
      } else {
        yPos += 8;
      }
      
      // Proposals Table
      if (doc.proposals && doc.proposals.length > 0) {
        const tableData = doc.proposals.map(proposal => [
          proposal.company || 'Unknown',
          (proposal.stance || 'N/A').toUpperCase(),
          proposal.subtopic_name || 'N/A',
          proposal.proposal_text || proposal.supporting_quote || 'No text available'
        ]);
        
        pdf.autoTable({
          startY: yPos,
          head: [['Company', 'Stance', 'Subtopic', 'Proposal Text']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: vodafoneRed,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 9
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 3,
            valign: 'top'
          },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 20 },
            2: { cellWidth: 40 },
            3: { cellWidth: 'auto', halign: 'left' }
          },
          margin: { left: 14, right: 14 },
          styles: {
            overflow: 'linebreak',
            cellWidth: 'wrap'
          }
        });
      }
      
      // Footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${i} of ${pageCount} | Generated ${new Date().toLocaleDateString()}`,
          14,
          pdf.internal.pageSize.height - 10
        );
      }
      
      // Save
      const filename = `${doc.document_id}_proposals_${selectedMeeting}_${selectedAgenda}.pdf`;
      pdf.save(filename);
      
      toast.success(`Downloaded ${doc.document_id} proposals`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleCompanyClick = (company) => {
    if (companyFilter === company) {
      // If clicking the same company, clear the filter
      setCompanyFilter('');
      toast.success('Company filter cleared');
    } else {
      // Set new company filter
      setCompanyFilter(company);
      toast.success(`Filtering by ${company}`);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCompanyFilter('');
  };

  const handleExportExcel = async () => {
    try {
      toast.loading('Generating Excel file...');
      
      const response = await fetch(
        `${API_BASE_URL}/agenda/export-excel?meeting=${encodeURIComponent(selectedMeeting)}&agenda=${encodeURIComponent(selectedAgenda)}`
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedMeeting}_${selectedAgenda.replace(/\./g, '_')}_documents.xlsx`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success('Excel file downloaded!');
      
    } catch (error) {
      toast.dismiss();
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  const totalProposals = filteredDocuments.reduce((sum, doc) => sum + doc.proposal_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vodafone-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadDocuments}
            className="px-4 py-2 bg-vodafone-600 text-white rounded-lg hover:bg-vodafone-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-charcoal-900">Documents</h1>
            <p className="text-sm text-gray-600 mt-1">
              Browse and download proposals by document
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-vodafone-600">{filteredDocuments.length}</div>
            <div className="text-sm text-gray-600">Documents</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by document ID or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vodafone-500 focus:border-vodafone-500"
              />
            </div>
          </div>

          {/* Company Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vodafone-500 focus:border-vodafone-500 appearance-none"
            >
              <option value="">All Companies</option>
              {allCompanies.map(company => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filters */}
        {(searchTerm || companyFilter) && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-vodafone-100 text-vodafone-800">
                Search: "{searchTerm}"
              </span>
            )}
            {companyFilter && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                {companyFilter}
              </span>
            )}
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear all
            </button>
          </div>
        )}

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <span>
                <strong className="text-charcoal-900">{filteredDocuments.length}</strong> documents
              </span>
              <span>•</span>
              <span>
                <strong className="text-charcoal-900">{totalProposals}</strong> total proposals
              </span>
              {documents.length !== filteredDocuments.length && (
                <>
                  <span>•</span>
                  <span className="text-vodafone-600">
                    Filtered from {documents.length} documents
                  </span>
                </>
              )}
            </div>
            
            {/* Export to Excel Button */}
            <button
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reading Order
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Why Read This?
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proposals
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subtopics
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>No documents found</p>
                    {(searchTerm || companyFilter) && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-sm text-vodafone-600 hover:text-vodafone-700"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc) => (
                  <tr key={doc.document_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ReadingOrderBadge 
                        readingOrder={doc.importance_score} 
                        category={doc.importance_category}
                      />
                    </td>
                    <td className="px-6 py-4" style={{ maxWidth: '500px' }}>
                      <div className="text-sm text-gray-700">
                        {doc.importance_reasoning || (
                          <span className="text-gray-400 italic">No reading guidance available</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 text-vodafone-600 mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-charcoal-900">
                            {doc.document_id}
                          </div>
                          {doc.title && (
                            <div className="text-xs text-gray-500 truncate max-w-xs" title={doc.title}>
                              {doc.title}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" style={{ maxWidth: '300px' }}>
                      {doc.company ? (
                        <div className="flex flex-wrap gap-1">
                          {doc.company.split(',').map((company, idx) => (
                            <CompanyPill 
                              key={idx}
                              company={company.trim()} 
                              onClick={() => handleCompanyClick(company.trim())}
                              highlighted={companyFilter === company.trim()}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Unknown</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        doc.proposal_count > 5 ? 'bg-vodafone-100 text-vodafone-800' :
                        doc.proposal_count > 0 ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {doc.proposal_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-600">
                        {doc.subtopics?.length || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewPDF(doc)}
                          className="p-2 text-gray-600 hover:text-vodafone-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View PDF"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => generateDocumentPDF(doc)}
                          disabled={doc.proposal_count === 0}
                          className={`p-2 rounded-lg transition-colors ${
                            doc.proposal_count === 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-vodafone-600 hover:bg-vodafone-50'
                          }`}
                          title={doc.proposal_count === 0 ? 'No proposals to download' : 'Download proposals as PDF'}
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF Document Viewer */}
      {documentViewer.isOpen && (
        <PDFDocumentViewer
          documentId={documentViewer.documentId}
          isOpen={documentViewer.isOpen}
          onClose={closeDocumentViewer}
          verbatimProposals={documentViewer.verbatimProposals}
          subtopicName={documentViewer.subtopicName}
          semanticGroupName={documentViewer.semanticGroupName}
        />
      )}
    </div>
  );
};

export default DocumentBrowser;

